/**
 * /app/fidelizacion — Módulo de negocio: puntos + gift cards.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CrudEmptyState, CrudShellHeader, Loading, Panel } from "@/components/ds";
import { ClinicalPageShell } from "@/components/clinical";
import { FidelizacionTour } from "@/components/ProductTour";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Loader2, Gift, Sparkles, Copy, Ban, CheckCircle2, TrendingUp, CreditCard } from "lucide-react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";

export const Route = createFileRoute("/app/fidelizacion")({
  validateSearch: (s: Record<string, unknown>) => {
    const t = typeof s.tab === "string" ? s.tab : "";
    const tab = t === "tarjetas" || t === "puntos" ? t : undefined;
    return { tab };
  },
  component: FidelizacionPage,
  head: () => ({ meta: [{ title: "Fidelización — Structura Market" }] }),
});

interface LoyaltyTierRule { name: string; minPoints: number; earnMultiplier: number }
interface LoyaltyConfig {
  enabled: boolean; earnRate: number; redeemRate: number; minRedeem: number;
  pointsPerApprovedReview?: number; autoApproveVerifiedReviews?: boolean;
  tiers?: LoyaltyTierRule[];
}
interface GiftCard {
  id: string; code: string; initialAmount: number; balance: number; status: string;
  recipientEmail: string | null; createdAt: string; expiresAt: string | null;
}

const pesos = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);

function StatCard({ icon: Icon, label, value, hint }: { icon: typeof Sparkles; label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/80 p-4">
      <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function FidelizacionPage() {
  const tid = useWorkspaceTenantId();
  const { tab } = Route.useSearch();
  const [cfg, setCfg] = useState<LoyaltyConfig | null>(null);
  const [cards, setCards] = useState<GiftCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [amount, setAmount] = useState("");
  const [recipient, setRecipient] = useState("");
  const [loyaltyLock, setLoyaltyLock] = useState<PlanApiErrorPayload | null>(null);
  const [giftLock, setGiftLock] = useState<PlanApiErrorPayload | null>(null);

  useEffect(() => {
    if (tab !== "tarjetas") return;
    document.getElementById("fidelizacion-tarjetas")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [tab]);

  useEffect(() => {
    if (!tid) return;
    setLoading(true);
    setLoyaltyLock(null);
    setGiftLock(null);
    Promise.all([
      apiGetJson<{ config: LoyaltyConfig }>("/api/loyalty", tid)
        .then((d) => setCfg(d.config))
        .catch((e: unknown) => {
          const err = e as Error & { planLock?: PlanApiErrorPayload };
          if (err.planLock) setLoyaltyLock(err.planLock);
        }),
      apiGetJson<{ giftCards: GiftCard[] }>("/api/giftcards", tid)
        .then((d) => setCards(d.giftCards ?? []))
        .catch((e: unknown) => {
          const err = e as Error & { planLock?: PlanApiErrorPayload };
          if (err.planLock) setGiftLock(err.planLock);
        }),
    ]).finally(() => setLoading(false));
  }, [tid]);

  const saveCfg = async (patch: Partial<LoyaltyConfig>) => {
    if (!cfg || !tid) return;
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSavingCfg(true);
    try {
      const d = await apiSendJson<{ config: LoyaltyConfig }>("/api/loyalty", "PATCH", patch, tid);
      setCfg(d.config);
    } catch (e) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) { setLoyaltyLock(err.planLock); return; }
      toast.error(err instanceof Error ? err.message : "Error");
    } finally {
      setSavingCfg(false);
    }
  };

  const issue = async () => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Ingresá un monto válido"); return; }
    setIssuing(true);
    try {
      const d = await apiSendJson<{ giftCard: GiftCard }>("/api/giftcards", "POST", {
        amount: amt, recipientEmail: recipient.trim() || undefined,
      }, tid);
      setCards((c) => [d.giftCard, ...c]);
      setAmount(""); setRecipient("");
      toast.success(`Gift card ${d.giftCard.code} emitida`);
    } catch (e) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) { setGiftLock(err.planLock); return; }
      toast.error(err instanceof Error ? err.message : "Error");
    } finally {
      setIssuing(false);
    }
  };

  const toggleStatus = async (card: GiftCard) => {
    const status = card.status === "disabled" ? "active" : "disabled";
    try {
      await apiSendJson("/api/giftcards?id=" + card.id, "PATCH", { status }, tid);
      setCards((cs) => cs.map((c) => c.id === card.id ? { ...c, status } : c));
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const activeCards = cards.filter((c) => c.status === "active" && c.balance > 0);
  const totalBalance = activeCards.reduce((s, c) => s + c.balance, 0);

  return (
    <div>
      <FidelizacionTour />
      <CrudShellHeader
        title="Fidelización"
        description="Puntos, beneficios y tarjetas de regalo"
        backHref="/app/clientes"
        backLabel="Volver a Clientes"
      />
      <ClinicalPageShell className="space-y-6">
        {loading ? (
          <Loading variant="dashboard" rows={2} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard
                icon={Sparkles}
                label="Programa de puntos"
                value={cfg?.enabled ? "Activo" : "Inactivo"}
                hint={cfg?.enabled ? `${cfg.earnRate} pts/$ · canje desde ${cfg.minRedeem}` : "Activá para retener clientes"}
              />
              <StatCard icon={TrendingUp} label="Tasa de canje" value={cfg ? `$${cfg.redeemRate}/punto` : "—"} />
              <StatCard icon={CreditCard} label="Tarjetas de regalo activas" value={String(activeCards.length)} />
              <StatCard icon={Gift} label="Saldo en circulación" value={pesos(totalBalance)} />
            </div>

            {loyaltyLock ? (
              <FeatureLockedState payload={loyaltyLock} />
            ) : cfg ? (
              <div data-tour="fidelizacion-loyalty">
              <Panel
                title="Campaña de puntos"
                description="Acumulación y canje en checkout"
                actions={<Switch checked={cfg.enabled} onCheckedChange={(v) => saveCfg({ enabled: v })} />}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Puntos por $1</Label>
                    <Input type="number" step="0.01" value={cfg.earnRate}
                      onChange={(e) => setCfg({ ...cfg, earnRate: parseFloat(e.target.value) || 0 })}
                      onBlur={(e) => saveCfg({ earnRate: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Valor del punto ($)</Label>
                    <Input type="number" step="0.01" value={cfg.redeemRate}
                      onChange={(e) => setCfg({ ...cfg, redeemRate: parseFloat(e.target.value) || 0 })}
                      onBlur={(e) => saveCfg({ redeemRate: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Mínimo canje</Label>
                    <Input type="number" value={cfg.minRedeem}
                      onChange={(e) => setCfg({ ...cfg, minRedeem: parseInt(e.target.value) || 0 })}
                      onBlur={(e) => saveCfg({ minRedeem: parseInt(e.target.value) || 0 })} />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Puntos por reseña aprobada</Label>
                    <Input
                      type="number"
                      value={cfg.pointsPerApprovedReview ?? 50}
                      onChange={(e) => setCfg({ ...cfg, pointsPerApprovedReview: parseInt(e.target.value) || 0 })}
                      onBlur={(e) => saveCfg({ pointsPerApprovedReview: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="flex items-end gap-3 pb-1">
                    <div className="space-y-1.5 flex-1">
                      <Label className="text-xs">Auto-aprobar reseñas verificadas</Label>
                      <p className="text-[11px] text-muted-foreground">Si está on, la reseña entra approved y suma puntos al crear.</p>
                    </div>
                    <Switch
                      checked={Boolean(cfg.autoApproveVerifiedReviews)}
                      onCheckedChange={(v) => saveCfg({ autoApproveVerifiedReviews: v })}
                    />
                  </div>
                </div>
                <div className="mt-4 space-y-2 rounded-lg border border-border/60 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">Niveles (loyalty_tiers)</p>
                      <p className="text-[11px] text-muted-foreground">
                        Thresholds → nombre y multiplicador de acumulación. Requiere capability loyalty_tiers.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const tiers = [
                          ...(cfg.tiers ?? []),
                          { name: "Nuevo", minPoints: 0, earnMultiplier: 1 },
                        ];
                        setCfg({ ...cfg, tiers });
                      }}
                    >
                      Agregar nivel
                    </Button>
                  </div>
                  {(cfg.tiers ?? []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sin niveles configurados.</p>
                  ) : (
                    <div className="space-y-2">
                      {(cfg.tiers ?? []).map((t, idx) => (
                        <div key={idx} className="grid grid-cols-3 gap-2 items-end">
                          <div className="space-y-1">
                            <Label className="text-[10px]">Nombre</Label>
                            <Input
                              value={t.name}
                              onChange={(e) => {
                                const tiers = [...(cfg.tiers ?? [])];
                                tiers[idx] = { ...t, name: e.target.value };
                                setCfg({ ...cfg, tiers });
                              }}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px]">Mín. puntos</Label>
                            <Input
                              type="number"
                              value={t.minPoints}
                              onChange={(e) => {
                                const tiers = [...(cfg.tiers ?? [])];
                                tiers[idx] = { ...t, minPoints: parseInt(e.target.value) || 0 };
                                setCfg({ ...cfg, tiers });
                              }}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px]">Multiplicador</Label>
                            <div className="flex gap-1">
                              <Input
                                type="number"
                                step="0.1"
                                value={t.earnMultiplier}
                                onChange={(e) => {
                                  const tiers = [...(cfg.tiers ?? [])];
                                  tiers[idx] = { ...t, earnMultiplier: parseFloat(e.target.value) || 1 };
                                  setCfg({ ...cfg, tiers });
                                }}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const tiers = (cfg.tiers ?? []).filter((_, i) => i !== idx);
                                  setCfg({ ...cfg, tiers });
                                }}
                              >
                                ✕
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => saveCfg({ tiers: cfg.tiers ?? [] })}
                        disabled={savingCfg}
                      >
                        Guardar niveles
                      </Button>
                    </div>
                  )}
                </div>
                {savingCfg && <p className="text-xs text-muted-foreground">Guardando…</p>}
              </Panel>
              </div>
            ) : null}

            <div data-tour="fidelizacion-giftcards" id="fidelizacion-tarjetas">
            <Panel title="Tarjetas de regalo">
              {giftLock ? (
                <FeatureLockedState payload={giftLock} compact capabilityCode="gift_cards" />
              ) : (
                <>
                  <div className="flex flex-wrap items-end gap-3 rounded-xl border p-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Monto</Label>
                      <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="5000" className="w-32" />
                    </div>
                    <div className="space-y-1.5 flex-1 min-w-[180px]">
                      <Label className="text-xs">Email destinatario (opcional)</Label>
                      <Input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="cliente@email.com" />
                    </div>
                    <Button onClick={issue} disabled={issuing} className="gap-2">
                      {issuing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />} Emitir
                    </Button>
                  </div>

                  {cards.length === 0 ? (
                    <CrudEmptyState
                      icon={<Gift className="h-8 w-8" />}
                      title="Todavía no emitiste tarjetas de regalo"
                      description="Creá la primera para regalar saldo a clientes."
                    />
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {cards.map((c) => (
                        <div key={c.id} className="rounded-xl border bg-card p-4 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <code className="text-sm font-bold">{c.code}</code>
                            <button type="button" onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Copiado"); }}>
                              <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                            </button>
                          </div>
                          <p className="text-2xl font-semibold tabular-nums text-primary">{pesos(c.balance)}</p>
                          <p className="text-xs text-muted-foreground">de {pesos(c.initialAmount)} inicial</p>
                          {c.recipientEmail && <p className="text-xs truncate text-muted-foreground">{c.recipientEmail}</p>}
                          <div className="flex items-center justify-between pt-1">
                            {c.status === "disabled" && <Badge variant="destructive">Deshabilitada</Badge>}
                            {c.status === "depleted" && <Badge variant="secondary">Sin saldo</Badge>}
                            {c.status === "active" && <Badge variant="default">Activa</Badge>}
                            <Button variant="ghost" size="sm" onClick={() => toggleStatus(c)} className="h-7 gap-1 text-xs">
                              {c.status === "disabled"
                                ? <><CheckCircle2 className="h-3 w-3" /> Activar</>
                                : <><Ban className="h-3 w-3" /> Pausar</>}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Panel>
            </div>
          </>
        )}
      </ClinicalPageShell>
    </div>
  );
}
